// Media-session keep-alive for WebRTC on backgrounded/locked mobile tabs.
//
// Problem: When iOS/Android locks the screen, browsers suspend JavaScript
// timers and can tear down WebRTC connections after ~15-30s of inactivity.
// A simple WS ping doesn't survive because the interval itself gets throttled.
//
// Solution: Register an active MediaSession backed by a silent looping audio
// element. iOS/Android's media stack recognizes this as an ongoing playback
// (like a music player or phone call) and grants "background audio"
// privileges — JS timers keep running, WS stays open, WebRTC audio keeps
// flowing, even with the screen off.
//
// This is the same technique used by Google Meet, Discord, and Zoom Web.
// Battery impact is negligible: it's the same cost as playing music in the
// background. The screen still sleeps normally per the user's setting.
//
// IMPORTANT: audio.play() requires a user gesture on iOS/Chrome. Call
// startMediaSessionKeepalive() from inside a click/tap/pointerdown handler.
// The exported armMediaSessionOnGesture() helper installs a one-shot
// listener that retries play() on the very next user interaction.

// 1 second of PCM silence, 8kHz mono. Small (~120 bytes base64), works in
// every browser that supports HTMLAudioElement. Volume is set to near-zero
// (iOS Safari ignores volume === 0 and stops the session).
const SILENT_WAV_DATA_URI =
  'data:audio/wav;base64,UklGRiQFAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAFAAA' +
  'AgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgIC' +
  'AgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgIC' +
  'AgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgIC' +
  'AgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgIA=';

let audioEl = null;
let started = false;
let armedGesture = false;
let externalOnPause = null;
let externalOnPlay = null;

const log = (...a) => {
  // eslint-disable-next-line no-console
  console.log('%c[mediaSession]', 'color:#c0f000;font-weight:bold', ...a);
};

/**
 * Start the media-session keep-alive. Must be called from a user-gesture
 * handler on iOS/Chrome or the initial audio.play() will be rejected
 * (armMediaSessionOnGesture() handles the retry).
 *
 * @param {object} opts
 * @param {string} opts.title    — Shown on lock screen (e.g. "Samtale24-7")
 * @param {string} opts.artist   — Shown as subtitle (e.g. "Live samtale")
 * @param {string} [opts.artwork] — Optional cover-art URL (192×192 PNG)
 * @param {function} [opts.onPause] — Called if user taps "pause" on lock screen
 * @param {function} [opts.onPlay]  — Called if user taps "play" on lock screen
 */
export function startMediaSessionKeepalive({ title = 'Samtale24-7', artist = 'Live samtale', artwork, onPause, onPlay } = {}) {
  if (started) return;
  started = true;
  externalOnPause = onPause || null;
  externalOnPlay = onPlay || null;

  audioEl = document.createElement('audio');
  audioEl.src = SILENT_WAV_DATA_URI;
  audioEl.loop = true;
  audioEl.volume = 0.001; // near-zero — iOS ignores exactly 0
  audioEl.setAttribute('playsinline', '');
  audioEl.setAttribute('data-media-session-keepalive', '1');
  audioEl.style.display = 'none';
  document.body.appendChild(audioEl);

  audioEl.play().then(() => {
    log('silent audio started — background media session active');
  }).catch(() => {
    log('audio.play() blocked — will retry on next user gesture');
    armMediaSessionOnGesture();
  });

  if ('mediaSession' in navigator) {
    try {
      const metadata = { title, artist };
      if (artwork) metadata.artwork = [{ src: artwork, sizes: '192x192', type: 'image/png' }];
      navigator.mediaSession.metadata = new window.MediaMetadata(metadata);
      // Handler for lock-screen "play" button — restart the loop if it stopped.
      navigator.mediaSession.setActionHandler('play', () => {
        try { audioEl?.play().catch(() => {}); } catch { /* noop */ }
        externalOnPlay?.();
      });
      // Handler for lock-screen "pause" button — DO NOT pause our loop
      // (that would kill the keepalive). Optionally invoke the app-supplied
      // callback (e.g. toggle mic mute).
      navigator.mediaSession.setActionHandler('pause', () => {
        externalOnPause?.();
      });
      navigator.mediaSession.playbackState = 'playing';
      log('MediaSession registered:', { title, artist });
    } catch (e) {
      log('MediaSession registration failed (non-fatal):', e?.message || e);
    }
  }
}

/**
 * Install a one-shot listener that retries audio.play() on the next user
 * gesture. Idempotent — safe to call multiple times.
 */
export function armMediaSessionOnGesture() {
  if (armedGesture) return;
  armedGesture = true;
  const handler = () => {
    if (audioEl) {
      audioEl.play()
        .then(() => log('silent audio started via gesture retry'))
        .catch((e) => log('gesture retry play() failed:', e?.message || e));
    }
    document.removeEventListener('pointerdown', handler);
    document.removeEventListener('touchstart', handler);
    document.removeEventListener('click', handler);
    armedGesture = false;
  };
  document.addEventListener('pointerdown', handler, { once: true, passive: true });
  document.addEventListener('touchstart', handler, { once: true, passive: true });
  document.addEventListener('click', handler, { once: true });
}

/** Stop the keep-alive and remove the audio element. Called on VideoRoom unmount. */
export function stopMediaSessionKeepalive() {
  if (!started) return;
  started = false;
  externalOnPause = null;
  externalOnPlay = null;
  try { audioEl?.pause(); audioEl?.remove(); } catch { /* noop */ }
  audioEl = null;
  if ('mediaSession' in navigator) {
    try {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
      navigator.mediaSession.playbackState = 'none';
      log('MediaSession released');
    } catch { /* noop */ }
  }
}
